/**
 * The audit trail's closed vocabulary (audit-trail template; REC-14,
 * PLT-02). Every value has a writer and every writer's literal is a value
 * — `audit-vocabulary.test.ts` holds the two to each other both ways, so
 * the list grows one PR at a time with the routes that write it, never
 * ahead of them (the template's pitfall 3: dead words).
 */
export const AUDIT_ACTIONS = [
  /** The authorisation layer refused a request; `details.permission` names the cell (permission-matrix.md §4). */
  "access.refused",
  "role.granted",
  "role.revoked",
  "user.created",
  "user.invited",
  /** The first sign-in after an invitation: invited → active (PLT-03). */
  "user.activated",
  "user.preferences_changed",
  "user.profile_changed",
  "user.disabled",
  "user.enabled",
  /** The 30-day clock started (DELETE /users/{id}); the sweep writes `user.removed` when it runs out. */
  "user.removal_requested",
  "user.removed",
  /** Cognito global sign-out after a senior role changes (permission-matrix.md §4.7) — an attempted/applied pair. */
  "session.revoked",
  /** An export left the system (data-classification.md §6). */
  "export.produced",
  /** An item was put in front of a person (CNT-10). */
  "assignment.created",
  /** An assignment was taken back, with its reason (CNT-12); any progress already made stays in the record. */
  "assignment.withdrawn",
  /**
   * The learner says they finished an item that runs on somebody else's platform (CNT-25). A claim, not a
   * completion: the hours ledger is not credited until somebody attests it, which is the next row.
   */
  "external_completion.claimed",
  /** An instructor or director attested a claimed completion (CNT-25); `authority` says whose say-so it was. */
  "external_completion.attested",
  /**
   * A person sent a ticket, a feature request or a bug report from the round ? (PLT-09). `details` carries the kind,
   * area, severity and the tenant's number — never the words they typed, which stay in the row (SEC-07).
   */
  "support_request.created",
  /**
   * The request became a case in the support desk's shared Connect Cases domain (ADR-0022) — an external effect, so an
   * `attempted` row then `applied` (with the case id and reference) or `failed` (with the error's name). The actor is
   * the job, `lms:support-handoff`; never the words.
   */
  "support_request.handed_off",
  /**
   * A tenant admin read the whole tenant's requests (`?all=true`). Metadata only since the owner's decision on
   * gairm/lms#220 (2026-09-23) — `details.view` is `metadata`; rows without it are from before, when the list carried
   * the words. Kept as a row because it is still everyone's requests read by one person, not because it is SEC-06.
   */
  "support_request.listed",
  /** A question was asked of the docs shelf (PLT-09): whether it was answered and what it cost — never the question. */
  "docs_question.asked",
  /**
   * A confirmed completion banked RTI hours in the ledger (CNT-26) — in the same transaction as the attestation, and
   * only when the assignment was made through a program's RTI-plan entry (REC-18).
   */
  "hours_entry.credited",
  /** A ledger entry was reversed, and optionally replaced, by new rows that name it (REC-01). */
  "hours_entry.corrected",
  /**
   * A message left through the email chain (PLT-07) — an external effect, so `attempted` then `applied` (with SES's
   * message id) or `failed` (the error's name). A send the chain would not make is a refusal row with its code:
   * `NOT_ALLOWED_HERE` (SEC-08: not on a non-prod environment's allow-list), `SUPPRESSED` (the address bounced or
   * complained before) or `NOT_CONFIGURED`. `details` names the template and the recipient's domain, never the address.
   */
  "email.sent",
  /**
   * An address went on the platform's suppression list (PLT-07): SES reported a permanent bounce or a complaint. In
   * `__platform`, by the job `lms:email-events`; the resource id is the address's SHA-256, never the address.
   */
  "email.suppressed",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * `authority` answers a different question from the actor: on whose say-so.
 * Never defaulted — "nobody approved this" and "no approval was needed"
 * must stay distinguishable in a log somebody will read adversarially.
 */
export const AUDIT_AUTHORITIES = ["not_required", "approved", "self_approved", "break_glass"] as const;
export type AuditAuthority = (typeof AUDIT_AUTHORITIES)[number];

/**
 * `applied` and `refused` are the two shapes for our own store; `attempted`
 * then `applied` or `failed` is the pair for an external effect (Cognito,
 * an email), paired by `correlation_id`. An `attempted` with no outcome row
 * is itself the alarm (the template's "the one to get right on day one").
 */
export const AUDIT_OUTCOMES = ["applied", "refused", "attempted", "failed"] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

/** What the actor is: a person in the tenant, or a script/job named by its principal. */
export interface AuditActor {
  /** The person row, when the actor is a signed-in person. */
  personId?: string;
  /** The identity provider's subject for a person, or the IAM principal ARN / `lms:<job>` for a non-human. */
  subject: string;
  /** The email of record for a person; absent for a job. */
  email?: string;
  /** The display name at the time, never a lookup key. */
  name?: string;
  /** The role the actor acted in, when one was resolved. */
  role?: string;
}

/** The scope a row belongs to — the tenant, one program, or the platform (design.md §14.8; no nullable foreign key). */
export type AuditScopeKind = "tenant" | "program" | "platform";

/** Primitives only — a free map is where a secret leaks one day (template pitfall 6). */
export type AuditDetails = Record<string, string | number | boolean | null>;

export interface AuditEventInput {
  action: AuditAction;
  actor: AuditActor;
  scope: { kind: AuditScopeKind; id: string };
  resource: { kind: string; id: string };
  authority: AuditAuthority;
  /** Required when authority is `approved`. */
  approvedBy?: string;
  outcome: AuditOutcome;
  /** Changed fields only, before → after, values compared by JSON. */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  details?: AuditDetails;
  /** Required on `pii.read` and on a refusal; free text that must not carry another person's data. */
  reason?: string;
  requestId?: string;
  /** Pairs an `attempted` row with its `applied`/`failed`; the writer mints one when absent. */
  correlationId?: string;
}

/** One row as the read route returns it (design.md §15, `GET /audit`). */
export interface AuditEventRow {
  id: string;
  occurredAt: string;
  action: string;
  actor: { personId?: string; subject: string; email?: string; name?: string; role?: string };
  scope: { kind: AuditScopeKind; id: string };
  resource: { kind: string; id: string };
  authority: AuditAuthority;
  approvedBy?: string;
  outcome: AuditOutcome;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  details?: AuditDetails;
  reason?: string;
  requestId?: string;
  correlationId: string;
  /** True when before/after/details were withheld because the resource is sensitive and the caller holds no pii.read. */
  redacted?: boolean;
}

export interface AuditListResponse {
  events: AuditEventRow[];
  /** The id to pass as `cursor` for the next page; absent on the last. */
  nextCursor?: string;
}
